import { Component, OnInit, OnDestroy, Inject, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { BlogService } from '../../_services/api/blog.service';
import { BlogList, BlogSearchCriteria } from '../../_models/blogs/blog.model';
import { ApiResponse } from '../../_models/api-response.model';
import { AdminBlogCard } from './admin-blog-card/admin-blog-card';
import { PaginationComponent } from '../../_shared_components/pagination/pagination.component';

@Component({
  selector: 'app-admin-blogs',
  standalone: true,
  imports: [CommonModule, FormsModule, AdminBlogCard, PaginationComponent],
  templateUrl: './admin-blogs.component.html',
  styleUrl: './admin-blogs.component.scss'
})
export class AdminBlogsComponent implements OnInit, OnDestroy {
  showFilterPopup: boolean = false;
  selectedCategory: string = 'all';

  blogs: BlogList[] = [];
  isLoading = false;
  currentPage = 0;
  pageSize = 10;
  totalCount = 0;
  totalPages = 0;
  
  searchTerm = '';
  private searchSubject = new Subject<string>();
  selectedVisibility: string = 'all';
  selectedSort: string = 'createdAt,desc';
  private destroy$ = new Subject<void>();

  constructor(
    private blogService: BlogService,    
    private router: Router,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.setupSearch();
    this.loadBlogs();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearch(): void {
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 0;
      this.loadBlogs();
    });
  }

  onSearch(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 0;
    this.loadBlogs();
  }

  onSortChange(): void {
    this.currentPage = 0;
    this.loadBlogs();
  }

  loadBlogs(): void {
    this.isLoading = true;
    
    const criteria: BlogSearchCriteria = {};
    if (this.searchTerm.trim()) {
      criteria.search = this.searchTerm.trim();
    }

    if (this.selectedVisibility !== 'all') {
      criteria.visible = this.selectedVisibility === 'true';
    }

    const [sortField, sortDirection] = this.selectedSort.split(',');
    this.blogService.getAllBlogs(criteria, this.currentPage, this.pageSize, sortField, sortDirection)
      .subscribe({
        next: (response: ApiResponse<any>) => {
          this.blogs = response.data?.content || [];
          this.totalCount = response.data?.totalElements || 0;
          this.totalPages = response.data?.totalPages || 0;
          setTimeout(() => {
            this.isLoading = false;
          }, 500);
        },
        error: (error) => {
          console.error('Error loading blogs:', error);
          this.isLoading = false;
        }
      });
  }

  addNewArticle(): void {
    this.router.navigate(['/admin/articles/create']);
  }

  onBlogUpdated(): void {
    this.loadBlogs();
  }

  onBlogDeleted(): void {
    this.loadBlogs();
  }

  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadBlogs();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadBlogs();
    }
  }

  onPageChange(page: number) {
    this.isLoading = true;
    this.currentPage = page;
    if (isPlatformBrowser(this.platformId)) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setTimeout(() => {
      this.loadBlogs();
    }, 1000);
  }
 
  toggleFilterPopup() {
    this.showFilterPopup = !this.showFilterPopup;
  }

  closeFilterPopup(event: Event) {
    if ((event.target as HTMLElement).classList.contains('filter-overlay')) {
      this.showFilterPopup = false;
    }
  }

  private updateQueryParams(params: any) {
    const queryParams = { ...this.route.snapshot.queryParams };
    
    Object.keys(params).forEach(key => {
      if (params[key] === null || params[key] === '') {
        delete queryParams[key];
      } else {
        queryParams[key] = params[key];
      }
    });

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}